import { CURSOR_STYLE, MOUSE_BUTTON } from './config';
import { PTZApiClient } from './PTZApiClient';
import { CoordinateTransformer } from './CoordinateTransformer';
import { CanvasDrawer } from './CanvasDrawer';

export class PTZRegion {
    #canvas;
    #digitalZoomWrapper;

    #apiClient;
    #transformer;
    #drawer;

    // Drawing state
    #state = {
        isDrawing: false,
        startPoint: null,
        endPoint: null,
        zoomMode: null,
        mouseButtonPressed: MOUSE_BUTTON.NONE,
        allowedArea: null,
        requestInProgress: false,
    };

    constructor(canvas, videoElement, videoContainer, digitalZoom, cameraId, setPTZRequestStatus) {
        this.#canvas = canvas;
        this.#digitalZoomWrapper = digitalZoom?.instance?.wrapperComponent ?? null;

        this.#apiClient = new PTZApiClient(cameraId, {
            maxRetries: 3,
            onStatusChange: setPTZRequestStatus,
        });
        this.#transformer = new CoordinateTransformer(videoElement, canvas);
        this.#drawer = new CanvasDrawer(canvas, videoContainer);
    }

    // ==================== State Management ====================

    #resetState() {
        this.#state = {
            isDrawing: false,
            startPoint: null,
            endPoint: null,
            zoomMode: null,
            mouseButtonPressed: MOUSE_BUTTON.NONE,
            allowedArea: this.#state.allowedArea,
            requestInProgress: false,
        };
        this.#drawer.setCursor(CURSOR_STYLE.DEFAULT);
        this.#drawer.clear();
    }

    #isMouseButtonPressed(button) {
        return this.#state.mouseButtonPressed === button;
    }

    #hasValidRect() {
        const { startPoint, endPoint } = this.#state;
        if (!startPoint || !endPoint) return false;
        return startPoint.x !== endPoint.x && startPoint.y !== endPoint.y;
    }

    // ==================== Event Listeners ====================

    #addEventListeners() {
        this.#canvas.addEventListener('mousedown', this.#onMouseDown);
        this.#canvas.addEventListener('mouseup', this.#onMouseUp);
        this.#canvas.addEventListener('mousemove', this.#onMouseMove);
        this.#canvas.addEventListener('wheel', this.#onWheel);
        this.#canvas.addEventListener('contextmenu', this.#onContextMenu);
        this.#canvas.addEventListener('mouseenter', this.#onMouseEnter);
    }

    #removeEventListeners() {
        this.#canvas.removeEventListener('mousedown', this.#onMouseDown);
        this.#canvas.removeEventListener('mouseup', this.#onMouseUp);
        this.#canvas.removeEventListener('mousemove', this.#onMouseMove);
        this.#canvas.removeEventListener('wheel', this.#onWheel);
        this.#canvas.removeEventListener('contextmenu', this.#onContextMenu);
        this.#canvas.removeEventListener('mouseenter', this.#onMouseEnter);
    }

    #onContextMenu = (e) => {
        e.preventDefault();
    };

    #onMouseEnter = () => {
        window.getSelection().empty();
    };

    #onWheel = (e) => {
        e.preventDefault();
        if (this.#state.requestInProgress) return;
        this.#passEventThrough(e, WheelEvent);
    };

    #onMouseDown = (e) => {
        e.preventDefault();
        if (this.#state.isDrawing || this.#state.requestInProgress) return;

        const button = e.button;
        if (button !== MOUSE_BUTTON.LEFT && button !== MOUSE_BUTTON.RIGHT) return;

        this.#state.mouseButtonPressed = button;

        if (this.#isMouseButtonPressed(MOUSE_BUTTON.LEFT)) {
            this.#passEventThrough(e, MouseEvent);
            return;
        }

        // Right click - start drawing
        this.#state.allowedArea = this.#transformer.calculateAllowedDrawingArea();
        const x = e.offsetX;
        const y = e.offsetY;

        if (!this.#transformer.isPointAllowed(x, y, this.#state.allowedArea)) return;

        this.#state.isDrawing = true;
        this.#state.startPoint = { x, y };
    };

    #onMouseUp = async (e) => {
        if (this.#state.requestInProgress || e.button !== MOUSE_BUTTON.RIGHT) return;

        if (this.#state.startPoint) {
            await this.#sendPTZRequest();
        }

        this.#resetState();
    };

    #onMouseMove = (e) => {
        e.preventDefault();
        if (this.#isMouseButtonPressed(MOUSE_BUTTON.NONE) || this.#state.requestInProgress) return;

        if (this.#isMouseButtonPressed(MOUSE_BUTTON.LEFT)) {
            this.#passEventThrough(e, MouseEvent);
            return;
        }

        if (!this.#state.isDrawing) return;

        const clamped = this.#transformer.clampToAllowedArea(e.offsetX, e.offsetY, this.#state.allowedArea);
        this.#state.endPoint = clamped;
        this.#state.zoomMode = this.#drawer.getZoomMode(clamped.x - this.#state.startPoint.x);

        this.#drawer.drawRect(this.#state.startPoint, this.#state.endPoint);
    };

    #passEventThrough(event, Constructor) {
        if (this.#state.isDrawing || !this.#digitalZoomWrapper) return;
        const newEvent = new Constructor(event.type, event);
        this.#digitalZoomWrapper.dispatchEvent(newEvent);
    }

    // ==================== API Requests ====================

    async #sendPTZRequest() {
        this.#state.requestInProgress = true;
        this.#drawer.setCursor(CURSOR_STYLE.LOADING);
        if (this.#hasValidRect()) {
            const coords = this.#transformer.toRegionCoords(this.#state.startPoint, this.#state.endPoint);
            await this.#apiClient.sendRegionRequest(coords);
        } else if (this.#state.startPoint) {
            const coords = this.#transformer.toRecenterCoords(this.#state.startPoint);
            await this.#apiClient.sendRecenterRequest(coords);
        }
        this.#drawer.setCursor(CURSOR_STYLE.DEFAULT);
        this.#state.requestInProgress = false;
    }

    // ==================== Public API ====================

    start = () => {
        this.#drawer.configure();
        this.#drawer.setDefaultStyle();
        this.#addEventListeners();
    };

    close = () => {
        this.#removeEventListeners();
        this.#apiClient.destroy();
        if (!this.#drawer.isInitialized()) return;
        this.#resetState();
    };

    goToPreset = async (preset) => {
        if (isNaN(preset)) return;
        this.#drawer.setCursor(CURSOR_STYLE.LOADING);
        await this.#apiClient.goToPreset(preset);
        this.#drawer.setCursor(CURSOR_STYLE.DEFAULT);
    };

    fullZoomIn = async () => {
        this.#drawer.setCursor(CURSOR_STYLE.LOADING);
        await this.#apiClient.sendFullZoomIn();
        this.#drawer.setCursor(CURSOR_STYLE.DEFAULT);
    };

    fullZoomOut = async () => {
        this.#drawer.setCursor(CURSOR_STYLE.LOADING);
        await this.#apiClient.sendFullZoomOut();
        this.#drawer.setCursor(CURSOR_STYLE.DEFAULT);
    };
}
